import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Typography, Paper } from "@mui/material";

export default function TutorialOverlay({ steps, open, onClose, onComplete, onStepChange }) {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        if (!open) {
            setIndex(0);
        }
    }, [open]);

    const targetRect = useMemo(() => {
        if (!open || !steps || steps.length === 0) return null;
        const selector = steps[index]?.selector;
        if (!selector) return null;
        const el = document.querySelector(selector);
        if (!el) return null;
        const rect = el.getBoundingClientRect();
        return rect;
    }, [open, steps, index]);

    useEffect(() => {
        if (!open || !steps || steps.length === 0) return;
        const selector = steps[index]?.selector;
        if (!selector) return;
        const el = document.querySelector(selector);
        if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        if (onStepChange) onStepChange(index);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index, open]);

    if (!open) return null;

    const current = steps[index];

    const goNext = () => {
        if (index < steps.length - 1) {
            setIndex(index + 1);
        } else {
            if (onComplete) onComplete();
            if (onClose) onClose();
        }
    };

    const skip = () => {
        if (onClose) onClose();
    };

    const goPrev = () => {
        if (index > 0) {
            setIndex(index - 1);
        }
    };

    const highlightStyle = targetRect ? {
        position: 'fixed',
        left: targetRect.left - 8,
        top: targetRect.top - 8,
        width: targetRect.width + 16,
        height: targetRect.height + 16,
        border: '2px solid #1976d2',
        borderRadius: 8,
        pointerEvents: 'none',
        zIndex: 1301
    } : {};

    const tooltipBelowTop = targetRect ? targetRect.bottom + 12 : 24;
    const tooltipAboveTop = targetRect ? Math.max(16, targetRect.top - 172) : 24; // 160 box height + 12 gap
    const preferBelow = targetRect ? tooltipBelowTop + 172 <= window.innerHeight : true;
    const tooltipTop = targetRect ? (preferBelow ? tooltipBelowTop : tooltipAboveTop) : 24;
    const tooltipLeft = targetRect ? Math.max(16, Math.min(window.innerWidth - 360, targetRect.left)) : 24;

    const tooltipStyle = {
        position: 'fixed',
        left: tooltipLeft,
        top: tooltipTop,
        zIndex: 1302,
        maxWidth: 340,
        padding: 12
    };

    return (
        <>
            {/* Backdrop */}
            <Box
                role="presentation"
                aria-hidden
                sx={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    zIndex: 1300
                }}
                onClick={skip}
            />
            {/* Highlight Box */}
            {targetRect && <Box sx={highlightStyle} />}
            {/* Tooltip */}
            <Paper elevation={3} sx={tooltipStyle}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{`Step ${index + 1} of ${steps.length}`}</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{current?.content}</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button variant="text" onClick={skip} aria-label="Skip tutorial">Skip</Button>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" onClick={goPrev} disabled={index === 0} aria-label="Previous step">Back</Button>
                        <Button variant="contained" onClick={goNext} aria-label={index === steps.length - 1 ? 'Finish tutorial' : 'Next step'}>
                            {index === steps.length - 1 ? 'Finish' : 'Next'}
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </>
    );
}


